import { Component, DestroyRef, inject } from '@angular/core';
import { API, PlaybackScheduleFullDTO } from 'src/app/services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { TagService } from '../../../services/tag.service';
import { PlaybackScheduleFullDTOAndTags } from '../../../models/taggable.model';
import { DiscussionService } from '../../../services/discussion.service';
import { TableWrapperOptions } from '../../../services/global/global-interfaces';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { HierarchyService } from '../../../services/transformation-helper/hierarchy.service';
import { isNumberEven } from '../../../services/global/global-methods';

@Component({
  selector: 'sb-page-schedules',
  templateUrl: './schedules.component.html',
  styleUrls: ['./schedules.component.scss'],
  standalone: false,
})
export class SchedulesComponent {
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly hierarchy = inject(HierarchyService);
  protected readonly isNumberEven = isNumberEven;

  schedules: PlaybackScheduleFullDTOAndTags[];
  loading: boolean;
  debounceTimer: ReturnType<typeof setTimeout>;
  options: TableWrapperOptions;

  showList: boolean;

  showChildren: Map<number, boolean> = new Map<number, boolean>();

  shareDragged: number;

  isDragging: boolean = false;

  constructor(
    private readonly api: API,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly tagService: TagService,
    public readonly discussionService: DiscussionService,
    public readonly modalService: ModalService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {
    this.showList = false;
    this.loading = true;
    this.schedules = [];
    this.debounceTimer = null;
  }

  requestDebounced(options?: TableWrapperOptions) {
    if (options) {
      this.options = options;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    if (this.options?.pagination) {
      this.loading = true;
      if (!this.showList) {
        const preconditionIds = Array.from(this.showChildren.keys());
        this.api
          .ss_nm_PlaybackSchedule_retrieveHierarchy(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.options.exactMatch, this.options.search),
            },
            this.options.pagination
          )
          .pipe(
            switchMap(shares => {
              this.schedules = shares;

              if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
                this.schedules = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.PlaybackSchedule, this.schedules);
              }
              return this.api.ss_nm_PlaybackSchedule_retrieveAll({ precondition: preconditionIds });
            }),
            take(1),
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loading = false;
              this.isDragging = false;
            })
          )
          .subscribe(schedules => {
            if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
              schedules = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.PlaybackSchedule, schedules);
            }
            const map = preconditionIds.map(preconditionId => {
              return { id: preconditionId, preconditions: [] };
            });
            schedules.forEach(schedule => {
              const toAdd = map.find(tempObj => tempObj.id === schedule.precondition?.id);
              if (toAdd?.preconditions) {
                toAdd.preconditions.push(schedule);
              }
            });
            for (const tempObj of map) {
              const searchSchedule = this.hierarchy.getObjectFromHierarchyArrayById(this.schedules, tempObj, 'preconditionOf');

              if (searchSchedule) {
                searchSchedule.preconditionOf = tempObj.preconditions;
              }
            }
          });
      } else {
        this.api
          .ss_nm_PlaybackSchedule_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.options.exactMatch, this.options.search),
            },
            this.options.pagination
          )
          .pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (data: PlaybackScheduleFullDTO[]) => {
              this.schedules = data;
              if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
                this.schedules = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.PlaybackSchedule, this.schedules);
              }
            },
            error: error => {
              this.errorHandler.errorHandler(error);
              this.schedules = [];
            },
          });
      }
    }
  }

  deleteSchedule(schedule: PlaybackScheduleFullDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.schedules.deleteSchedule'),
        message: this.translate.instant('lang.schedules.deleteScheduleMessage', { itemName: schedule.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.loading = true;
          this.api
            .ss_nm_PlaybackSchedule_deletePlaybackSchedule(schedule.id)
            .pipe(
              take(1),
              finalize(() => {
                this.loading = false;
              })
            )
            .subscribe({
              next: () => {
                this.requestDebounced();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  private updateNetworkShareHierarchy(currentSchedule: PlaybackScheduleFullDTO): void {
    this.loading = true;
    this.api
      .ss_nm_PlaybackSchedule_retrieveHierarchy({
        precondition: currentSchedule.id,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.isDragging = false;
        })
      )
      .subscribe({
        next: (e: PlaybackScheduleFullDTO[]) => {
          currentSchedule.preconditionOf = e;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  toggleShowChildren(schedule: PlaybackScheduleFullDTO) {
    if (this.showChildren.has(schedule.id)) {
      this.showChildren.delete(schedule.id);
    } else {
      this.showChildren.set(schedule.id, true);
      this.updateNetworkShareHierarchy(schedule);
    }
  }

  onmousedown($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'true');
  }

  onmouseup($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'false');
  }

  onDragStart($event: any) {
    $event.dataTransfer.setData('scheduleid', $event.target.attributes['data-schedule'].value);
    $event.dataTransfer.dropEffect = 'move';
    this.shareDragged = +$event.target.attributes['data-schedule'].value;
  }

  onDragEnd($event: any) {
    this.isDragging = false;
  }

  onDragOver($event: any) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedSchedule: number = +this.shareDragged;
    const targetSchedule: number = target.attributes['data-schedule'] ? +target.attributes['data-schedule'].value : null;

    if (draggedSchedule === 0) {
      return;
    }

    if (draggedSchedule === targetSchedule) {
      return;
    }

    const draggedScheduleObj: PlaybackScheduleFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.schedules,
      { id: draggedSchedule },
      'preconditionOf'
    );
    const targetShareObj: PlaybackScheduleFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.schedules,
      { id: targetSchedule },
      'preconditionOf'
    );

    if (!this.allowSubDrop(draggedScheduleObj, targetShareObj)) {
      return;
    }

    $event.preventDefault();
  }

  allowSubDrop(draggedScheduleObj: PlaybackScheduleFullDTO, targetScheduleObj: PlaybackScheduleFullDTO): boolean {
    if (!targetScheduleObj) {
      return true;
    }
    let currentSchedule: PlaybackScheduleFullDTO = targetScheduleObj;

    while (currentSchedule) {
      if (currentSchedule.id === draggedScheduleObj.id) {
        return false;
      }
      currentSchedule = this.hierarchy.getObjectFromHierarchyArrayById(this.schedules, currentSchedule.precondition, 'preconditionOf');
    }
    return true;
  }

  onDrop($event: any, index?: number) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedSchedule: number = +this.shareDragged;
    const targetSchedule: number = target.attributes['data-schedule'] ? +target.attributes['data-schedule'].value : null;

    const draggedScheduleObj: PlaybackScheduleFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.schedules,
      { id: draggedSchedule },
      'preconditionOf'
    );
    const targetScheduleObj: PlaybackScheduleFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.schedules,
      { id: targetSchedule },
      'preconditionOf'
    );

    this.hierarchy.removeObjectFromHierarchyArrayById(this.schedules, draggedScheduleObj, 'preconditionOf');

    if (targetScheduleObj) {
      if (index != null) {
        targetScheduleObj.priority = index;
        targetScheduleObj.preconditionOf.splice(index, 0, draggedScheduleObj);
      } else {
        targetScheduleObj.preconditionOf.push(draggedScheduleObj);
      }
      if (!this.showChildren.has(targetScheduleObj.id)) {
        this.removeVisibility(targetScheduleObj);
      }
    } else {
      if (index != null) {
        draggedScheduleObj.priority = this.schedules[index]
          ? this.schedules[index].priority
          : this.schedules[this.schedules.length - 1].priority + 1;
      }
      this.schedules.push(draggedScheduleObj);
    }

    this.refreshParents();

    this.refreshShareDragged(draggedScheduleObj);
  }

  private removeVisibility(schedule: PlaybackScheduleFullDTO) {
    this.showChildren.delete(schedule.id);
    const traversedChildren = this.traverseChildren(schedule, []);
    traversedChildren?.forEach(child => this.showChildren.delete(child.id));
  }

  traverseChildren(schedule: PlaybackScheduleFullDTO, result: PlaybackScheduleFullDTO[]): PlaybackScheduleFullDTO[] {
    if (!schedule.preconditionOf || !Array.isArray(schedule.preconditionOf)) {
      return result;
    }
    result.push(...schedule.preconditionOf);
    for (const child of schedule.preconditionOf) {
      this.traverseChildren(child, result);
    }
    return result;
  }

  refreshShareDragged(draggedScheduleObj: PlaybackScheduleFullDTO) {
    const tempSchedule: PlaybackScheduleFullDTO = { ...draggedScheduleObj };
    tempSchedule.preconditionOf = [];

    if (tempSchedule?.precondition) {
      tempSchedule.precondition.preconditionOf = [];
      tempSchedule.precondition.precondition = null;
    }

    this.api
      .ss_nm_PlaybackSchedule_updateById(tempSchedule)
      .pipe(take(1))
      .subscribe(() => {
        this.refreshFromServer();
      });
  }

  refreshParents() {
    for (const share of this.schedules) {
      this.refreshParent(share, null);
    }
  }

  refreshParent(schedule: PlaybackScheduleFullDTO, parent?: PlaybackScheduleFullDTO) {
    schedule.precondition = parent;
    if (schedule.preconditionOf && Array.isArray(schedule.preconditionOf)) {
      for (const child of schedule.preconditionOf) {
        this.refreshParent(child, schedule);
      }
    }
  }

  toggleListView() {
    this.showList = !this.showList;
    this.loading = true;
    this.requestDebounced();
  }
}
